import { IAlert, IDevice, IGroup, IHistoryLog, ISubscriber, ISubscriberTimelineItem, NotificationTypes } from '@/types';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useNotificationsStore } from './notifications.store';
import axios from 'axios';
import { useAuthStore } from './auth.store';

const rawDataToSubscriber = (data: any, id: string): ISubscriber => ({
  id,
  accountId: typeof data?.accountId === 'number' ? data.accountId : undefined,
  insuranceId: String(data?.insuranceId || ''),
  group: data?.devices?.[0]?.groupIds?.[0],
  created: +data?.created || 0,
  name: String(data?.name || ''),
  lastName: String(data?.lastName || ''),
  email: String(data?.email || ''),
  phone: String(data?.phone || ''),
  sms_phone: String(data?.sms_phone || ''),
  active: !!data.active,
  customerType: data?.customerType || 'insurance',
  devices: data.devices,
  address: {
    address1: String(data?.address1 || ''),
    address2: String(data?.address2 || ''),
    city: String(data?.city || ''),
    state: String(data?.state || ''),
    zipCode: String(data?.zipCode || ''),
  },
  delivery_address: {
    address1: String(data?.delivery_address1 || ''),
    address2: String(data?.delivery_address2 || ''),
    city: String(data?.delivery_city || ''),
    state: String(data?.delivery_state || ''),
    zipCode: String(data?.delivery_zipCode || ''),
  },
  notifications: Array.isArray(data?.notifications) && data?.notifications?.map((el: any) => ({
    name: String(el?.name || ''),
    email: String(el?.email || ''),
    phone: String(el?.phone || ''),
  }))
});

const apiBase = import.meta.env.VITE_API_URL;

export const useSubscribersStore = defineStore('subscribersStore', () => {
  const subscribers = ref<ISubscriber[]>([]);

  /** Increment to trigger timeline refetch (e.g. after sending test SMS) */
  const timelineRefreshTrigger = ref(0);
  const triggerTimelineRefresh = () => { timelineRefreshTrigger.value += 1; };

  const devices = computed<IDevice[]>(() => {
    const result: IDevice[] = [];

    subscribers.value.forEach(s => {
      if (s.devices) {
        s.devices.forEach(d => {
          result.push(d);
        });
      }
    })

    return result;
  });

  const alerts = computed<IAlert[]>(() => {
    const result: IAlert[] = [];

    subscribers.value.forEach(s => {
      if (s.devices) {
        s.devices.forEach(device => {
          if (device.alerts) {
            result.push(...device.alerts.map(a => ({ 
              ...a, 
              groupsIds: device.groupIds, 
              state: device.device_state,
              sudId: s.id,
              accountId: s.accountId,
              name: s.name,
              lastName: s.lastName,
              zipCode: s.address?.zipCode || device.device_zipCode || '',
              phone: s.phone
            })));
          }
        });
      }
    })


    return result;
  });






  /* ======== BANNERS ======== */
  const storedBannersData = JSON.parse(localStorage.getItem('bannersData') || '{}');

  const bannersData = ref({
    orderPending: storedBannersData.orderPending || [],
    inTransit: storedBannersData.inTransit || [],
    runningLate: storedBannersData.runningLate || [],
    issue: storedBannersData.issue || [],
    delivered: storedBannersData.delivered || [],
  });

  const setBannerData = (key: keyof typeof bannersData.value, data: IDevice[]) => {
    bannersData.value[key] = data;
    localStorage.setItem('bannersData', JSON.stringify(bannersData.value));
  };

  const allTogetherBannerData = computed(() => {
    return [
      ...bannersData.value.orderPending,
      ...bannersData.value.inTransit,
      ...bannersData.value.runningLate,
      ...bannersData.value.issue,
      ...bannersData.value.delivered,
    ];
  });
  /* ========================= */





  const subscribersMap = computed<Map<string, ISubscriber>>(() => new Map(subscribers.value.map(s => [s.id, s])));
  const notificationsStore = useNotificationsStore();
  const authStore = useAuthStore();

  const subscribersFetched = ref<boolean>(false);
  const subscribersLoading = ref<boolean>(false);
  const fetchSubscribers = (): Promise<void> => {
    console.log('[SubscribersStore] Starting fetchSubscribers...');
    return new Promise((resolve, reject) => {

      if (!subscribersFetched.value) {
        console.log('[SubscribersStore] First fetch, loading data...');
        subscribersFetched.value = true;
        subscribersLoading.value = true;

        axios<{ subscribers: ISubscriber[], totalCount: number }>({
          method: 'GET',
          url: apiBase + `/subscribers/`,
          headers: {
            Authorization: 'Bearer ' + authStore.idAuthToken,
          },
        })
          .then((result) => {
            console.log('[SubscribersStore] First batch received:', {
              batchSize: result.data.subscribers.length,
              totalCount: result.data.totalCount
            });
            
            const { subscribers: firstBatch, totalCount } = result.data;

            if (firstBatch.length >= totalCount) {
              console.log('[SubscribersStore] All data received in first batch');
              subscribers.value = firstBatch.map((s) => rawDataToSubscriber(s, s.id));
              console.log('[SubscribersStore] Data processed:', {
                subscribersCount: subscribers.value.length,
                devicesCount: subscribers.value.reduce((acc, s) => acc + (s.devices?.length || 0), 0)
              });
              resolve();
              return;
            }

            const limit = 1000;
            const remainingCount = totalCount - firstBatch.length;
            const additionalRequests = Math.ceil(remainingCount / limit);

            const requestPromises = [];

            for (let i = 1; i <= additionalRequests; i++) {
              const offset = i * limit;
              requestPromises.push(
                axios<{ subscribers: ISubscriber[], totalCount: number }>({
                  method: 'GET',
                  url: apiBase + `/subscribers/?limit=${limit}&offset=${offset}`,
                  headers: {
                    Authorization: 'Bearer ' + authStore.idAuthToken,
                  },
                })
              );
            }

            Promise.all(requestPromises)
              .then((additionalResults) => {
                const allSubscribers = [
                  ...firstBatch,
                  ...additionalResults.flatMap(res => res.data.subscribers)
                ];

                subscribers.value = allSubscribers.map((s) => rawDataToSubscriber(s, s.id));
                resolve();
              })
              .catch(err => {
                subscribersFetched.value = false;
                console.error('[SubscribersStore] Error fetching additional batches:', {
                  error: err.message,
                  status: err.response?.status,
                  statusText: err.response?.statusText,
                  data: err.response?.data
                });
                notificationsStore.error('Load Subscribers Failed');
                reject(err);
              });
          })
          .catch(err => {
            subscribersFetched.value = false;
            console.error('[SubscribersStore] Error fetching first batch:', {
              error: err.message,
              status: err.response?.status,
              statusText: err.response?.statusText,
              data: err.response?.data
            });
            notificationsStore.error('Load Subscribers Failed');
            reject(err);
          })
          .finally(() => {
            subscribersLoading.value = false;
          });
      } else {
        resolve();
      }
    });
  };

  const fetchSubscriber = (id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      axios<ISubscriber>({
        method: 'GET',
        url: apiBase + `/subscribers/${id}`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
      })
        .then((result) => {
          if (result.data) {
            const newSubscriber: ISubscriber = rawDataToSubscriber(result.data, result.data.id);
            const newSubscribers = subscribers.value;

            if (subscribersMap.value.has(id)) {
              const indexOfSubscriber: number = newSubscribers.findIndex(s => s.id === id);

              if (indexOfSubscriber > -1) {
                newSubscribers[indexOfSubscriber] = newSubscriber;
              }
            } else {
              newSubscribers.push(newSubscriber);
            }

            subscribers.value = [...newSubscribers];

            resolve();
          } else {
            resolve();
          }
        })
        .catch(err => {
          console.error(err);
          reject(err);
        })
    });
  };

  const editSubscriber = (subscriber: ISubscriber): Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'PUT',
        url: apiBase + `/subscribers/${subscriber.id}`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: subscriber
      })
        .then(() => {
          fetchSubscriber(subscriber.id)
            .finally(() => {
              resolve();
            })
        })
        .catch(err => {
          console.error(err);
          notificationsStore.error('Error');
          reject(err);
        })
    });
  };

  const editDevice = (device: IDevice): Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'PUT',
        url: apiBase + `/subscribers/${authStore.subscriber.id}/devices/${device.id}`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: device
      })
        .then(() => {
          fetchSubscriber(authStore.subscriber.id)
            .finally(() => {
              resolve();
            })
        })
        .catch(err => {
          console.error(err);
          notificationsStore.error('Edit Device Failed');
          reject(err);
        })
    });
  };

  const updateDeviceIMEI = (deviceId: string, imei: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'PATCH',
        url: apiBase + `/subscribers/${authStore.subscriber.id}/devices/${deviceId}/imei`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: { deviceIMEI: imei }
      })
        .then(() => {
          // Update local device data
          const localDevice = authStore.subscriber.devices?.find(d => String(d.id) === deviceId);
          if (localDevice) {
            localDevice.deviceIMEI = imei;
          }
          notificationsStore.showNotification('Device IMEI updated successfully', NotificationTypes.Success);
          resolve();
        })
        .catch(err => {
          console.error(err);
          notificationsStore.error('Failed to update device IMEI');
          reject(err);
        })
    });
  };

  const addNewDevice = (deviceData: Partial<IDevice>): Promise<IDevice> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'POST',
        url: apiBase + `/subscribers/${authStore.subscriber.id}/devices`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: deviceData
      })
        .then((result) => {
          const newDevice = result.data;
          notificationsStore.showNotification('New device added successfully', NotificationTypes.Success);
          fetchSubscriber(authStore.subscriber.id).finally(() => {
            resolve(newDevice);
          });
        })
        .catch(err => {
          console.error(err);
          const errorMessage = err?.response?.data?.message || 
                              (err?.response?.data?.error === 'taken' ? 'This IMEI is already registered to another device' : 'Failed to add new device');
          notificationsStore.error(errorMessage);
          reject(err);
        })
    });
  };

  const contact = (comment: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'POST',
        url: apiBase + `/subscribers/${authStore.subscriber.id}/contact`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: { comment }
      })
        .then(() => {
          resolve();
        })
        .catch(err => {
          console.error(err);
          notificationsStore.error('Error');
          reject(err);
        })
    });
  };

  const fetchSubscriberAlerts = (id: string): Promise<IAlert[]> => {
    return new Promise((resolve, reject) => {
      axios<IAlert[]>({
        method: 'GET',
        url: apiBase + `/subscribers/${id}/alerts`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
      })
        .then((result) => {
          if (result.data) {
            resolve(result.data);
          } else {
            resolve([]);
          }
        })
        .catch(err => {
          console.error(err);
          reject(err);
        })
    });
  };

  const fetchSubscriberHistoryLog = (id: string): Promise<IHistoryLog[]> => {
    return new Promise((resolve, reject) => {
      axios<IHistoryLog[]>({
        method: 'GET',
        url: apiBase + `/subscribers/${id}/historyLog`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
      })
        .then((result) => {
          if (result.data) {
            resolve(result.data);
          } else {
            resolve([]);
          }
        })
        .catch(err => {
          console.error(err);
          reject(err);
        })
    });
  };

  const fetchSubscriberTimeline = (id: string): Promise<ISubscriberTimelineItem[]> => {
    return new Promise((resolve, reject) => {
      axios<ISubscriberTimelineItem[]>({
        method: 'GET',
        url: apiBase + `/subscribers/${id}/notes`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
      })
        .then((result) => {
          if (result.data) {
            resolve(result.data);
          } else {
            resolve([]);
          }
        })
        .catch(err => {
          console.error(err);
          reject(err);
        })
    });
  };

  const addSubscriberTimelineItem = (id: string, note: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'POST',
        url: apiBase + `/subscribers/${id}/notes`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: { note }
      })
        .then(() => {
          resolve();
        })
        .catch(err => {
          console.error(err);
          reject(err);
        })
    });
  };

  const getSubscriberWithToken = (token: string): Promise<ISubscriber> => {
    return new Promise((resolve, reject) => {
      axios<ISubscriber>({
        method: 'GET',
        url: apiBase + `/subscribers/withToken?t=${token}`,
      })
        .then((result) => {
          if (result.data) {
            const newSubscriber: ISubscriber = rawDataToSubscriber(result.data, result.data.id);
            resolve(newSubscriber);
          } else {
            reject();
          }
        })
        .catch(err => {
          console.error(err);
          reject(err);
        })
    });
  };

  const answerWithToken = (token: string, answer: 'p' | 'n' | 'o'): Promise<string> => {
    return new Promise((resolve, reject) => {
      axios<string>({
        method: 'POST',
        url: apiBase + `/subscribers/answerWithToken?t=${token}`,
        data: {
          answer
        }
      })
        .then((result) => {
          resolve(result.data);
        })
        .catch(err => {
          console.error(err);
          reject(err);
        })
    });
  };

  const sendEditEmailCode = (uid: string, email: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'PUT',
        url: apiBase + `/subscribers/${uid}/edit-email-code`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: {
          email,
        }
      })
        .then(() => {
          resolve();
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Error');
          reject(err);
        });
    });
  };

  const editSubscriberEmail = (uid: string, email: string, code: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'PUT',
        url: apiBase + `/subscribers/${uid}/email`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: {
          email,
          code
        },
      })
        .then(() => {
          fetchSubscriber(uid)
            .then(() => {
              resolve();
            })
            .catch(err => {
              console.error(err);
              reject(err);
            });
        })
        .catch((err) => {
          if (err?.response?.data === 'invalid code') {
            notificationsStore.error('Wrong Code');
            reject('taken');
          } else {
            console.error(err);
            notificationsStore.error('Edit Subscriber Failed');
            reject(err);
          }
        });
    });
  };

  return { subscribers, subscribersLoading, devices, alerts, subscribersMap, timelineRefreshTrigger, triggerTimelineRefresh, answerWithToken, contact, editSubscriber, editDevice, updateDeviceIMEI, addNewDevice, fetchSubscribers, getSubscriberWithToken, fetchSubscriber, fetchSubscriberAlerts, fetchSubscriberHistoryLog, fetchSubscriberTimeline, addSubscriberTimelineItem, bannersData, allTogetherBannerData, setBannerData, sendEditEmailCode, editSubscriberEmail };
});